import { HocuspocusProvider } from '@hocuspocus/provider'
import CollaborationCaret from '@tiptap/extension-collaboration-caret'
import { yCursorPlugin } from '@tiptap/y-tiptap'
import { User } from '../../../users.service'
import { constructNameForUser } from '../../../utils'
import { hslToHex } from '../utils/hsl-to-hex'

export type Awareness = NonNullable<HocuspocusProvider['awareness']>

export type CaretUser = { id: number | null; name: string; color: string }

/** The caret identity of the signed-in user, in a random pastel colour. */
export function buildCaretUser(user: User | null): CaretUser {
  return {
    id: user?.id ?? null,
    name: user !== null ? constructNameForUser(user) : 'Unknown User',
    color: hslToHex(Math.floor(Math.random() * 360), 0.75, 0.8),
  }
}

function userIdOf(state: Record<string, any> | null | undefined): number | null {
  const userId = state?.['user']?.id
  return typeof userId === 'number' ? userId : null
}

function lastUpdatedOf(awareness: Awareness, clientId: number): number {
  return awareness.meta.get(clientId)?.lastUpdated ?? 0
}

/**
 * Whether `clientId`, whose awareness is `state`, is the session to draw a caret for. A user can hold several sessions
 * — another tab, or a reloaded page whose state has yet to be removed — so each user is drawn once, at their most
 * recently active session, and not at all when they are the local user, whose own caret is the editor's selection.
 */
export function isLatestRemoteSessionOfUser(awareness: Awareness, localClientId: number, clientId: number, state: Record<string, any> | null): boolean {
  if (clientId === localClientId) {
    return false
  }

  const userId = userIdOf(state)
  if (userId === null) {
    return true
  }
  if (userId === userIdOf(awareness.getLocalState())) {
    return false
  }

  const lastUpdated = lastUpdatedOf(awareness, clientId)
  return Array.from(awareness.getStates()).every(([otherClientId, otherState]) => {
    const otherLastUpdated = lastUpdatedOf(awareness, otherClientId)
    return (
      otherClientId === clientId ||
      userIdOf(otherState) !== userId ||
      otherLastUpdated < lastUpdated ||
      (otherLastUpdated === lastUpdated && otherClientId < clientId)
    )
  })
}

/** {@link CollaborationCaret} drawing a single caret per user, keyed by the `id` in each session's awareness `user`. */
export const UniqueUserCollaborationCaret = CollaborationCaret.extend({
  addProseMirrorPlugins() {
    const [awarenessListener] = this.parent?.() ?? []
    const awareness: Awareness = this.options.provider.awareness

    return [
      awarenessListener,
      yCursorPlugin(awareness, {
        awarenessStateFilter: (localClientId: number, clientId: number, state: Record<string, any> | null) =>
          isLatestRemoteSessionOfUser(awareness, localClientId, clientId, state),
        cursorBuilder: (user) => this.options.render(user),
        selectionBuilder: (user) => this.options.selectionRender(user),
      }),
    ]
  },
})
